import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES, buildRoute } from '../constants/routes';
import { renderBlogMarkdown } from '../utils/blogMarkdown';
import {
  useGetBlogArticleBySlugQuery,
  useListBlogArticlesQuery,
  useListBlogCategoriesQuery,
} from '../store/siteContentApi';
import imgLipid from '../assets/img/v5/guide-lipid.jpg';
import imgHba1c from '../assets/img/v5/guide-hba1c.jpg';
import imgThyroid from '../assets/img/v5/guide-thyroid.jpg';
import imgPrep from '../assets/img/v5/guide-prep.jpg';

// Guide Article — the reference's article page, replacing the old blog
// modal at /blog/:slug.
//
// The record gives us more than the reference assumed:
//
//   · `cta_test` carries the test name, code and **mrp**, so the dark
//     sidebar card is real and the reference's `₹ [price]` placeholder
//     is gone.
//   · `body_markdown` is rendered by the existing blogMarkdown renderer,
//     which already supports the two special blocks the reference draws
//     by hand — `::: numbers` becomes its four-value card grid and
//     `::: highlight` its "Key takeaways" panel. Both are styled below so
//     an admin writing those blocks gets the reference's layout.
//   · the "In this guide" nav is generated from the body's own headings
//     rather than hardcoded, since every article has different ones.
//
// Two things the reference has that we cannot fill:
//
//   · a named reviewer ("Dr. [Name], MD Pathology") — the reference
//     leaves it a placeholder and we have no author field, so the byline
//     says what the guides index already says: reviewed by a TrustLab
//     pathologist. API-NOTES 2.32.
//   · a cover photograph (2.29). The four reference photographs are
//     keyed to the category so an article's hero is at least stable.

const PHOTOS = [imgLipid, imgHba1c, imgThyroid, imgPrep];

const stripEmoji = (label) => String(label || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();

const slugify = (s) => String(s)
  .toLowerCase()
  .replace(/<[^>]+>/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

// Give every heading in the rendered body an id, and collect them for the
// sidebar nav. The renderer emits h3/h4, and raw-HTML articles bring their
// own h3s, so both are picked up.
const withHeadingIds = (html) => {
  const toc = [];
  const used = new Set();
  const out = String(html).replace(
    /<(h[234])(\s[^>]*)?>([\s\S]*?)<\/\1>/g,
    (match, tag, attrs = '', inner) => {
      const text = inner.replace(/<[^>]+>/g, '').trim();
      if (!text) return match;
      let id = slugify(text) || `section-${toc.length + 1}`;
      while (used.has(id)) id = `${id}-${toc.length + 1}`;
      used.add(id);
      toc.push({ id, text });
      // Keep any attributes the author wrote, minus an id we would clash with.
      const keep = String(attrs || '').replace(/\sid="[^"]*"/g, '');
      return `<${tag} id="${id}"${keep}>${inner}</${tag}>`;
    },
  );
  return { html: out, toc };
};

// ── the article's own prose styling ───────────────────────────────────
const prose = {
  fontFamily: UI,
  minWidth: 0,
  overflowWrap: 'anywhere',
  '& p': { m: 0, mb: 2.25, fontSize: 18, lineHeight: 1.75, color: '#2E4540', textWrap: 'pretty' },
  '& p:last-child': { mb: 0 },
  '& h3, & h4': {
    fontFamily: DISPLAY,
    fontWeight: 700,
    fontSize: 'clamp(24px, 3vw, 32px)',
    letterSpacing: '-.02em',
    lineHeight: 1.1,
    color: 'var(--tl-g9)',
    m: '24px 0 12px',
    scrollMarginTop: '110px',
  },
  '& h4': { fontSize: 'clamp(20px, 2.4vw, 25px)' },
  '& ul, & ol': { m: 0, mb: 2.25, pl: 2.75, display: 'flex', flexDirection: 'column', gap: 1.25 },
  '& li': { fontSize: 18, lineHeight: 1.65, color: '#2E4540', textWrap: 'pretty' },
  '& li::marker': { color: 'var(--tl-teal)', fontWeight: 800 },
  '& strong': { color: 'var(--tl-g9)', fontWeight: 800 },
  '& em': { fontStyle: 'italic' },
  '& a': { color: 'var(--tl-teal-d)', fontWeight: 700 },
  '& img': { width: '100%', borderRadius: '22px', display: 'block', my: 2 },
  '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto', borderCollapse: 'collapse', fontSize: 16, mb: 2.25 },
  '& th, & td': { textAlign: 'left', p: '10px 12px', borderBottom: '1px solid #EEF3F1' },

  // ::: numbers — the reference's four-value card grid.
  '& .numbers-row': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
    gap: 1.25,
    mb: 2.25,
  },
  '& .num-box': {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    p: 2.5,
    borderRadius: '22px',
    bgcolor: 'var(--tl-mint)',
  },
  '& .nb-val': { fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, color: 'var(--tl-g9)' },
  '& .nb-label': { fontSize: 14, lineHeight: 1.5, color: '#3E5550' },

  // ::: highlight — the reference's "Key takeaways" panel.
  '& .highlight-box': {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    p: 'clamp(20px, 3.4vw, 32px)',
    my: 2.25,
    borderRadius: '28px',
    bgcolor: 'var(--tl-teal)',
    color: '#FFFFFF',
    '& p, & li': { color: '#FFFFFF', fontSize: 16, lineHeight: 1.6 },
    '& p': { mb: 0 },
    '& ul, & ol': { mb: 0 },
    '& strong': { color: '#FFFFFF' },
  },
  '& .hl-label': {
    fontFamily: DISPLAY,
    fontWeight: 700,
    fontSize: 22,
    lineHeight: 1.15,
    letterSpacing: '-.01em',
  },
};

const panel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const GuideArticle = () => {
  const { slug } = useParams();
  const { data: article, isLoading, isError } = useGetBlogArticleBySlugQuery(slug, { skip: !slug });
  const { data: articles = [] } = useListBlogArticlesQuery();
  const { data: categories = [] } = useListBlogCategoriesQuery();

  const labelFor = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.slug] = stripEmoji(c.label) || c.slug; });
    return map;
  }, [categories]);

  const photoFor = useMemo(() => {
    const map = {};
    categories.forEach((c, i) => { map[c.slug] = PHOTOS[i % PHOTOS.length]; });
    return map;
  }, [categories]);

  const { html, toc } = useMemo(
    () => withHeadingIds(renderBlogMarkdown(article?.body_markdown)),
    [article?.body_markdown],
  );

  // Keep reading: others in the same category first, topped up from the rest.
  const keepReading = useMemo(() => {
    const others = articles.filter((a) => a.slug !== slug);
    const same = others.filter((a) => a.category_slug === article?.category_slug);
    return [...same, ...others.filter((a) => a.category_slug !== article?.category_slug)].slice(0, 3);
  }, [articles, slug, article?.category_slug]);

  // The record always has a `cta_test` object, but for the five
  // Respiratory articles it is blank — empty name, no test_id, mrp 0.
  // Rendering it gives a nameless card priced at ₹ 0, so a blank one
  // counts as no CTA at all. API-NOTES 2.33.
  const rawCta = article?.cta_test || (article?.cta_tests || [])[0] || null;
  const cta = rawCta && String(rawCta.test_name || '').trim() ? rawCta : null;
  const category = article ? labelFor[article.category_slug] : '';
  const hero = (article && photoFor[article.category_slug]) || PHOTOS[0];
  const updated = article?.updatedAt || article?.published_at;

  if (!isLoading && (isError || !article)) {
    return (
      <Box component="main" sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 2, pt: 6, pb: 12, display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'flex-start' }}>
        <Typography component="h1" sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1, letterSpacing: '-.03em', color: 'var(--tl-g9)' }}>
          We can&rsquo;t find that guide.
        </Typography>
        <Typography sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
          The link may be old, or the guide may have been taken down.
        </Typography>
        <Box component={Link} to={ROUTES.BLOG} sx={{ borderRadius: '16px', p: '15px 22px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF', fontFamily: UI, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}>
          All health guides
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 2, pt: 2, pb: 9, display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {/* ── header ─────────────────────────────────────────────────── */}
      <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 860, pt: 1.5, px: 1 }}>
        {isLoading ? (
          <>
            <Box sx={{ width: 110, height: 28, borderRadius: '999px', bgcolor: '#FFFFFF' }} />
            <Box sx={{ height: 54, width: '88%', borderRadius: '10px', bgcolor: '#FFFFFF' }} />
            <Box sx={{ height: 20, width: '46%', borderRadius: '8px', bgcolor: '#FFFFFF' }} />
          </>
        ) : (
          <>
            {category && (
              <Box
                component={Link}
                to={ROUTES.BLOG}
                sx={{
                  alignSelf: 'start',
                  p: '6px 10px',
                  borderRadius: '999px',
                  bgcolor: 'var(--tl-mint)',
                  color: 'var(--tl-teal-d)',
                  fontFamily: UI,
                  fontSize: 12,
                  fontWeight: 800,
                  textDecoration: 'none',
                }}
              >
                {category}
              </Box>
            )}
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(32px, 5vw, 64px)',
                lineHeight: 0.98,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: 'var(--tl-g9)',
              }}
            >
              {article.title}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                fontFamily: UI,
                fontSize: 14,
                color: 'var(--tl-muted-2)',
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#D5EFE8', color: 'var(--tl-teal-d)', display: 'grid', placeItems: 'center', fontSize: 16, fontWeight: 800 }}
              >
                ✓
              </Box>
              <Box component="span">
                Reviewed by a <Box component="strong" sx={{ color: 'var(--tl-g9)', fontWeight: 800 }}>TrustLab pathologist</Box>
              </Box>
              {article.read_time && <><span>·</span><Box component="span">{article.read_time}</Box></>}
              {updated && (
                <>
                  <span>·</span>
                  <Box component="span">
                    Updated {new Date(updated).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box
        component="img"
        src={hero}
        alt=""
        sx={{ width: '100%', aspectRatio: '21 / 9', minHeight: 220, objectFit: 'cover', borderRadius: '32px', display: 'block' }}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 5 }}>
        {/* ── the guide ────────────────────────────────────────────── */}
        <Box
          component="article"
          sx={{ flex: '2 1 560px', minWidth: 0, maxWidth: 740, p: 'clamp(24px, 4vw, 48px)', borderRadius: '32px', bgcolor: '#FFFFFF' }}
        >
          {isLoading && [0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ height: i % 3 === 0 ? 26 : 16, width: `${96 - (i % 4) * 11}%`, borderRadius: '8px', bgcolor: 'var(--tl-mint)', mb: 1.75 }} />
          ))}

          {!isLoading && article.excerpt && (
            <Typography sx={{ m: 0, mb: 2.5, fontFamily: UI, fontSize: 20, fontWeight: 600, lineHeight: 1.6, color: 'var(--tl-g9)', textWrap: 'pretty' }}>
              {article.excerpt}
            </Typography>
          )}

          {!isLoading && <Box sx={prose} dangerouslySetInnerHTML={{ __html: html }} />}

          {!isLoading && (
            <Typography sx={{ m: 0, mt: 3, fontFamily: UI, fontSize: 13, lineHeight: 1.6, color: '#7D918C' }}>
              This guide is for general information and does not replace advice from your doctor.
            </Typography>
          )}
        </Box>

        {/* ── aside ────────────────────────────────────────────────── */}
        <Box
          component="aside"
          sx={{ flex: '1 1 280px', minWidth: 0, position: 'sticky', top: 100, display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {toc.length > 1 && (
            <Box component="nav" sx={{ ...panel, gap: 0.5 }}>
              <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, pb: 0.75, color: 'var(--tl-g9)' }}>
                In this guide
              </Box>
              {toc.map((t) => (
                <Box
                  key={t.id}
                  component="a"
                  href={`#${t.id}`}
                  sx={{
                    p: '8px 0',
                    borderTop: '1px solid #EEF3F1',
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--tl-g9)',
                    textDecoration: 'none',
                    '&:hover': { color: 'var(--tl-teal-d)' },
                  }}
                >
                  {t.text}
                </Box>
              ))}
            </Box>
          )}

          {cta && (
            <Box sx={{ ...panel, bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, lineHeight: 1.15, letterSpacing: '-.01em' }}>
                {cta.test_name}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: '#C7DDD7' }}>
                {[cta.specialisation, cta.test_code, 'doctor-verified'].filter(Boolean).join(' · ')}
              </Box>
              {Number(cta.mrp) > 0 && (
                <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 30 }}>
                  ₹ {Number(cta.mrp).toLocaleString('en-IN')}
                </Box>
              )}
              <Box
                component={Link}
                to={cta.test_id ? buildRoute(ROUTES.TEST_DETAIL, { id: cta.test_id }) : ROUTES.TESTS}
                sx={{
                  borderRadius: '16px',
                  p: '15px 22px',
                  bgcolor: 'var(--tl-yellow)',
                  color: 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 16,
                  fontWeight: 800,
                  textAlign: 'center',
                  textDecoration: 'none',
                  '&:hover': { bgcolor: 'var(--tl-yellow-d)' },
                }}
              >
                Book at home
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── keep reading ───────────────────────────────────────────── */}
      {keepReading.length > 0 && (
        <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 5 }}>
          <Box
            component="span"
            sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3vw, 36px)', lineHeight: 1.05, letterSpacing: '-.02em', px: 1, color: 'var(--tl-g9)' }}
          >
            Keep reading
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
              gap: 1.5,
            }}
          >
            {/* Keyed by position, not category: these three are usually
                all from one category, and keying by category gave three
                identical photographs in a row. */}
            {keepReading.map((a, i) => (
              <Box
                key={a.slug}
                component={Link}
                to={`${ROUTES.BLOG}/${a.slug}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '28px',
                  overflow: 'hidden',
                  bgcolor: '#FFFFFF',
                  color: 'var(--tl-g9)',
                  textDecoration: 'none',
                  transition: 'box-shadow .2s var(--tl-ease)',
                  '&:hover': { boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)' },
                }}
              >
                <Box
                  component="img"
                  src={PHOTOS[i % PHOTOS.length]}
                  alt=""
                  sx={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', display: 'block' }}
                />
                <Box
                  component="span"
                  sx={{ p: '18px 20px 22px', fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, lineHeight: 1.15, letterSpacing: '-.01em' }}
                >
                  {a.title}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GuideArticle;
